import { useState } from "react";
import { ContractModel, CounterPartySubContractorViewModel, CounterPartyViewModel } from "../../../Contract.types";
import { ICounterParty } from "./CounterParty";
import { getContractQueryKey } from "../../../Contract.hooks";
import { deleteHttp, post } from "../../../../../common/api/apiShared";
import { useDotLegalQueryClient, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export default function useCounterPartyHooks(props: ICounterParty) {
    const queryClient = useDotLegalQueryClient();

    const [removingSubContractorId, setRemovingSubContractorId] = useState("");
    const [showSubContractors, setShowSubContractors] = useState(false);
    const queryKey = getContractQueryKey(props.contractId);

    const stepDataUpdater = {
        onLegalEntityChange: (legalEntityId: string | null) => {
            if (!legalEntityId) {
                props.onClearParty();
                return;
            }

            props.onCounterPartyChange(props.counterParty.id, (counterParty: CounterPartyViewModel) => {
                counterParty.legalEntityId = legalEntityId;
            });
        },
        onSubContractorChange: (subContratorId: string, legalEntityId: string | null, link?: string) => {
            props.onSubContractorChange(props.counterParty.id, subContratorId, (subContractor: CounterPartySubContractorViewModel) => {
                subContractor.legalEntityId = legalEntityId ?? undefined;
                subContractor.link = link;
            });
        },
        onHasAgreementChange: (subContratorId: string, hasAgreement: boolean) => {
            props.onSubContractorChange(props.counterParty.id, subContratorId, (subContractor: CounterPartySubContractorViewModel) => {
                subContractor.hasAgreement = hasAgreement;
            });
        },
    };

    const addSubContractorMutation = useDotLegalMutation(addSubContractorAPI, {
        onSuccess: (data) => {
            const contract = data.value();
            queryClient.setQueryData(queryKey, contract);
        },
    });

    const addSubContractor = async (isLink: boolean) => {
        await addSubContractorMutation.mutateAsync(isLink);
    };

    async function addSubContractorAPI(isLink: boolean) {
        return await post<ContractModel>(`/contract/${props.contractId}/counterParties/${props.counterParty.id}/subcontractor`, isLink);
    }

    const removeSubContractorMutation = useDotLegalMutation(removeSubContractorAPI, {
        onSuccess: (data) => {
            const contract = data.value();
            queryClient.setQueryData(queryKey, contract);
            setRemovingSubContractorId("");
        },
    });

    const removeSubContractor = async (id: string) => {
        setRemovingSubContractorId(id);
        await removeSubContractorMutation.mutateAsync(id);
    };

    async function removeSubContractorAPI(id: string) {
        return await deleteHttp(`/contract/${props.contractId}/counterParties/${props.counterParty.id}/subcontractor/${id}`);
    }

    return {
        stepDataUpdater,
        showSubContractors,
        setShowSubContractors,
        addSubContractor,
        isAddingSubContractor: addSubContractorMutation.isLoading,
        removeSubContractor,
        removingSubContractorId,
    };
}
